import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BlogLayout from "@/components/BlogLayout";
import GhostSubscribeForm from "@/components/GhostSubscribeForm";
import useSEO from "@/hooks/useSEO";
import { getPost, formatDate } from "@/lib/ghost";
import "@/styles/ghost-prose.css";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    getPost(slug)
      .then((p) => {
        if (!p) setNotFound(true);
        else setPost(p);
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [slug]);

  // Always call the hook (no conditional hooks). Values update once post loads.
  useSEO({
    title: post ? `${post.title} — ProofMark Blog` : "Post — ProofMark Blog",
    description: post?.excerpt || "",
    path: `/blog/${slug}`,
    ogImage: post?.feature_image || undefined,
  });

  // Article JSON-LD — injected once the post data is available.
  // Follows the same pattern as glossary DefinedTerm injection.
  useEffect(() => {
    if (!post) return;
    const BASE = "https://proofmark-frontend-production.up.railway.app";
    const scriptId = "proofmark-jsonld-article";
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt || "",
      datePublished: post.published_at,
      dateModified: post.updated_at || post.published_at,
      author: {
        "@type": "Person",
        name: post.primary_author?.name || "ProofMark",
      },
      publisher: {
        "@type": "Organization",
        name: "ProofMark",
        url: BASE,
      },
      ...(post.feature_image ? { image: post.feature_image } : {}),
      url: `${BASE}/blog/${post.slug}`,
    });
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [post]);

  if (!loading && notFound) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <BlogLayout>
      <Link
        to="/blog"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        All posts
      </Link>

      {loading && (
        <div className="py-24 text-center text-sm text-muted-foreground">Loading…</div>
      )}

      {post && (
        <article>
          {/* Article header */}
          <header className="mb-8">
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-4">
                {post.tags.map((tag) => (
                  <Link
                    key={tag.slug}
                    to={`/blog?tag=${tag.slug}`}
                    className="tracked-caps text-brand hover:opacity-75 transition-opacity"
                  >
                    {tag.name}
                  </Link>
                ))}
              </div>
            )}
            <h1 className="font-serif-display text-4xl leading-tight mb-5">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {post.primary_author?.name && (
                <span>{post.primary_author.name}</span>
              )}
              <span>{formatDate(post.published_at)}</span>
              {post.reading_time > 0 && (
                <span>{post.reading_time} min read</span>
              )}
            </div>
          </header>

          {/* Feature image */}
          {post.feature_image && (
            <img
              src={post.feature_image}
              alt={post.title}
              className="w-full h-72 object-cover rounded-lg mb-10"
            />
          )}

          {/* Ghost HTML body */}
          <div
            className="ghost-content"
            dangerouslySetInnerHTML={{ __html: post.html || "" }}
          />

          {/* Subscribe CTA */}
          <div className="mt-16 pt-10 border-t border-border">
            <p className="tracked-caps mb-2">Enjoyed this?</p>
            <h2 className="font-serif-display text-2xl mb-1">
              Get new posts in your inbox
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
              Thinking on AI governance, review workflows, and compliance patterns —
              delivered when we publish.
            </p>
            <GhostSubscribeForm />
          </div>

          {/* Back link */}
          <div className="mt-10">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              All posts
            </Link>
          </div>
        </article>
      )}
    </BlogLayout>
  );
}
